import { useAuth0 } from '@auth0/auth0-react';
import axios, { AxiosInstance } from 'axios';
import { useEffect, useRef, useState } from 'react';

import {
  VITE_INDUCTION_URL,
  VITE_LASTMILE_URL,
} from '@utils/envs';
import { TIME_ZONE } from '@helpers/constants';

import useInductionRequest, { InductionRequest } from './rest/use-induction-request';
import useLastMileRequest, { LastMileRequest } from './rest/use-lastmile-request';

export interface HttpProviderContext {
  httpIsReady: boolean;
  induction: InductionRequest;
  lastmile: LastMileRequest;
}

export function useHttpProvider(): HttpProviderContext {
  const { getAccessTokenSilently, isAuthenticated, isLoading } = useAuth0();
  const [httpIsReady, setHttpIsReady] = useState(false);

  const inductionRef = useRef<AxiosInstance | null>(null);
  const lastmileRef = useRef<AxiosInstance | null>(null);

  const induction = useInductionRequest(inductionRef);
  const lastmile = useLastMileRequest(lastmileRef);

  useEffect(() => {
    if (isLoading) return;

    const userAgentInterceptor = (config: any) => {
      try {
        const email: string | null = localStorage.getItem('email');
        const s: string | null = localStorage.getItem('station');
        let station: string = '';
        if (s) {
          try {
            station = JSON.parse(s).station;
          } catch (e) {
            console.warn('Error parsing station from localStorage', e);
          }
        }
        config.headers['X-User-Agent'] = `${email || ''}/${station || ''}`;
      } catch (error) {
        console.error('Error setting X-User-Agent', error);
      }
      return config;
    };

    // 1. Induction Instance
    const indInst = axios.create({
      baseURL: VITE_INDUCTION_URL,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
    });
    indInst.interceptors.request.use(userAgentInterceptor);
    inductionRef.current = indInst;

    // 2. LastMile Instance
    const lmInst = axios.create({
      baseURL: VITE_LASTMILE_URL,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        timezone: TIME_ZONE,
      },
    });
    lmInst.interceptors.request.use(userAgentInterceptor);
    // Add Auth0 Token Interceptor only if authenticated
    if (isAuthenticated) {
      lmInst.interceptors.request.use(async (config) => {
        try {
          const token = await getAccessTokenSilently({});
          if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
          }
        } catch (error) {
          console.error('Error getting access token', error);
        }
        return config;
      });
    }
    lastmileRef.current = lmInst;

    setHttpIsReady(true);

  }, [getAccessTokenSilently, isAuthenticated, isLoading]);

  return {
    httpIsReady,
    induction,
    lastmile,
  };
}